import { useEffect, useRef, useState } from "react";
import { Calendar, Check, ChevronDown } from "lucide-react";
import {
  DashboardRange,
  PRESET_LABELS,
  PRESET_OPTIONS,
  rangeLabel,
} from "../../types/dashboardRange";

interface DashboardFilterProps {
  value: DashboardRange;
  onChange: (range: DashboardRange) => void;
}

const todayStr = () => new Date().toISOString().slice(0, 10);

export function DashboardFilter({ value, onChange }: DashboardFilterProps) {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(value.from ?? todayStr());
  const [customTo, setCustomTo] = useState(value.to ?? todayStr());
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selectPreset = (preset: DashboardRange["preset"]) => {
    onChange({ preset });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    // Guard against reversed ranges.
    const from = customFrom <= customTo ? customFrom : customTo;
    const to = customFrom <= customTo ? customTo : customFrom;
    onChange({ preset: "custom", from, to });
    setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 bg-white/70 backdrop-blur-xl ring-1 ring-gray-900/[0.05] rounded-xl px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white/90 transition-colors"
      >
        <Calendar className="w-4 h-4 text-indigo-500" />
        <span className="truncate max-w-[160px]">{rangeLabel(value)}</span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 p-2">
          {PRESET_OPTIONS.map((preset) => (
            <button
              key={preset}
              onClick={() => selectPreset(preset)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg hover:bg-indigo-50 text-gray-700"
            >
              <span>{PRESET_LABELS[preset]}</span>
              {value.preset === preset && <Check className="w-4 h-4 text-indigo-600" />}
            </button>
          ))}

          <div className="my-2 border-t border-gray-100" />

          <div className="px-2 pb-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Custom range
              </span>
              {value.preset === "custom" && <Check className="w-4 h-4 text-indigo-600" />}
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[11px] text-gray-500">
                From
                <input
                  type="date"
                  value={customFrom}
                  max={customTo || todayStr()}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="mt-0.5 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </label>
              <label className="text-[11px] text-gray-500">
                To
                <input
                  type="date"
                  value={customTo}
                  max={todayStr()}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="mt-0.5 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </label>
              <button
                onClick={applyCustom}
                disabled={!customFrom || !customTo}
                className="mt-1 w-full bg-indigo-600 text-white text-sm font-medium rounded-lg py-2 hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
